import { useQuery } from '@tanstack/react-query';
import api from '../../http';
import { useForBindingId } from '../../../forBinding';
import type { SoilAnalysesResponse } from './types';

interface UseGetSoilAnalysesParams {
	areaId?: string;
	startDate?: string;
	endDate?: string;
	filter?: string;
	sortAsc?: string | boolean;
	oldest?: boolean;
	page?: number;
	limit?: number;
	enabled?: boolean;
}

export const useGetSoilAnalyses = ({
	areaId,
	startDate,
	endDate,
	filter,
	sortAsc,
	oldest,
	page = 1,
	limit = 100,
	enabled = true,
}: UseGetSoilAnalysesParams = {}) => {
	const forBinding = useForBindingId();
	const query = useQuery({
		queryKey: [
			'soilAnalysis',
			forBinding,
			areaId,
			startDate,
			endDate,
			filter,
			sortAsc,
			page,
			limit,
			oldest,
		],
		enabled,
		placeholderData: (previousData, previousQuery) =>
			previousQuery?.queryKey[1] === forBinding ? previousData : undefined,
		queryFn: async () => {
			const params = new URLSearchParams();
			params.set('page', String(page));
			params.set('limit', String(limit));

			if (areaId) params.set('areaId', areaId);
			if (forBinding) params.set('forBinding', forBinding);
			if (startDate) params.set('startDate', startDate);
			if (endDate) params.set('endDate', endDate);
			if (filter) params.set('filter', filter);
			if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));
			if (oldest !== undefined) params.set('oldest', String(oldest));

			const { data } = await api.get<SoilAnalysesResponse>('/soil-analysis/findAll', {
				params,
			});
			return data;
		},
	});

	return {
		...query,
		data: query.data?.docs ?? [],
		pagination: query.data
			? {
					totalDocs: query.data.totalDocs,
					totalPages: query.data.totalPages,
					page: query.data.page,
					hasPrevPage: query.data.hasPrevPage,
					hasNextPage: query.data.hasNextPage,
					prevPage: query.data.prevPage,
					nextPage: query.data.nextPage,
				}
			: null,
	};
};
